import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/laceData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeIdx, setActiveIdx] = useState(0);

  const allCats = categories;
  const catIndex = allCats.findIndex((c) => c.slug === slug);
  const currentCat = catIndex >= 0 ? allCats[catIndex] : allCats[0];

  useEffect(() => {
    if (catIndex >= 0) setActiveIdx(catIndex);
  }, [slug, catIndex]);

  useEffect(() => {
    document.title = `${currentCat.name} | 蕾丝雅集 Lace Elegance`;
  }, [currentCat]);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: currentCat.name,
    description: currentCat.description,
    url: window.location.origin + __BASE_PATH__ + `/category/${currentCat.slug}`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: window.location.origin + __BASE_PATH__ + '/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: currentCat.name,
        },
      ],
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <div className="pt-[72px]">
        <div className="mx-auto px-4 md:px-6" style={{ maxWidth: '1280px' }}>
          <Breadcrumb items={[{ label: currentCat.name }]} />
        </div>

        <section className="relative w-full h-[350px] md:h-[420px] overflow-hidden">
          <img
            src={currentCat.image}
            alt={currentCat.name}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/50"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="font-heading text-2xl md:text-4xl font-semibold text-background-50 mb-3">
              {currentCat.name}
            </h1>
            <p className="text-background-200/90 text-sm md:text-base max-w-xl leading-relaxed">
              {currentCat.description}
            </p>
          </div>
        </section>

        <div className="mx-auto px-4 md:px-6 py-10" style={{ maxWidth: '1280px' }}>
          <div className="flex flex-wrap gap-2 mb-10 justify-center">
            {allCats.map((cat, idx) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveIdx(idx)}
                className={`px-4 py-2 text-sm rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeIdx === idx
                    ? 'bg-primary-500 text-background-50 font-medium'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mb-14">
            {currentCat.items.map((item) => (
              <div
                key={item.name}
                className="bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-accent-300 transition-all duration-300"
              >
                <div className="h-48 md:h-52 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 md:p-5">
                  <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2">
                    {item.name}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-6">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
              {currentCat.articles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group flex flex-col sm:flex-row bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="sm:w-48 h-40 sm:h-auto overflow-hidden flex-shrink-0">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4 md:p-5 flex flex-col justify-center">
                    <span className="text-xs text-foreground-500 mb-2">{article.date}</span>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}